import {
  CountryConfigStatus,
  EventLogResourceType,
  EventLogType,
  createCountryConfigScopeFieldSchemas,
  createCountryConfigScopeSchema,
  createPermitConfigFieldSchemas,
  createPermitConfigSchema,
  updateCountryConfigSchema,
  updateCountryConfigScopeFieldSchemas,
  updateCountryConfigScopeSchema,
  updatePermitConfigFieldSchemas,
  updatePermitConfigSchema
} from "../chunk-4OZDID3X.mjs";
import {
  OrganizationType,
  countryConfigScopeConfigSchema,
  permitConfigConfigSchema
} from "../chunk-ZGJRONN4.mjs";
import {
  fieldSchemas
} from "../chunk-DS3TDJHF.mjs";
import "../chunk-2USE66YR.mjs";

// src/tenant-api/routes.ts
var tenantApiRoutes = {
  /**
   * Country Config - Document Types endpoints
   * Self-managed document types (MODE 1: SELF-MANAGED)
   */
  countryConfig: {
    documentTypes: {
      /** GET /country-config/document-types - List all document types for tenant */
      list: () => "/country-config/document-types",
      /** GET /country-config/document-types/by-resource-type/:resourceType - Get document types by resource type */
      byResourceType: (resourceType) => `/country-config/document-types/by-resource-type/${resourceType}`,
      /** GET /country-config/document-types/:id - Get document type by ID */
      getById: (id) => `/country-config/document-types/${id}`,
      /** POST /country-config/document-types - Create new document type */
      create: () => "/country-config/document-types",
      /** PUT /country-config/document-types/:id - Update document type */
      update: (id) => `/country-config/document-types/${id}`,
      /** DELETE /country-config/document-types/:id - Delete document type */
      delete: (id) => `/country-config/document-types/${id}`
    },
    /**
     * FIR (Flight Information Region) endpoints
     * Self-managed FIRs (MODE 1: SELF-MANAGED)
     */
    firs: {
      /** GET /country-config/firs - List all FIRs for tenant */
      list: () => "/country-config/firs",
      /** GET /country-config/firs/:id - Get FIR by ID */
      getById: (id) => `/country-config/firs/${id}`,
      /** POST /country-config/firs - Create new FIR */
      create: () => "/country-config/firs",
      /** PUT /country-config/firs/:id - Update FIR */
      update: (id) => `/country-config/firs/${id}`,
      /** DELETE /country-config/firs/:id - Delete FIR */
      delete: (id) => `/country-config/firs/${id}`
    },
    /**
     * Airport endpoints
     * Self-managed airports (MODE 1: SELF-MANAGED)
     */
    airports: {
      /** GET /country-config/airports - List all airports for tenant */
      list: () => "/country-config/airports",
      /** GET /country-config/airports/:id - Get airport by ID */
      getById: (id) => `/country-config/airports/${id}`,
      /** POST /country-config/airports - Create new airport */
      create: () => "/country-config/airports",
      /** PUT /country-config/airports/:id - Update airport */
      update: (id) => `/country-config/airports/${id}`,
      /** DELETE /country-config/airports/:id - Delete airport */
      delete: (id) => `/country-config/airports/${id}`
    },
    /**
     * Country Config endpoints
     * Self-managed country configurations (MODE 1: SELF-MANAGED)
     */
    configs: {
      /** GET /country-configs - List all country configs for tenant */
      list: () => "/country-configs",
      /** GET /country-configs/:id - Get country config by ID */
      getById: (id) => `/country-configs/${id}`,
      /** GET /country-configs/:id/firs - Get FIRs for country config */
      firs: (id) => `/country-configs/${id}/firs`,
      /** GET /country-configs/:id/event-logs - Get event logs for country config */
      eventLogs: (id) => `/country-configs/${id}/event-logs`,
      /** POST /country-configs - Create new country config */
      create: () => "/country-configs",
      /** PATCH /country-configs/:id - Update country config */
      update: (id) => `/country-configs/${id}`,
      /** PATCH /country-configs/:id/publish - Publish country config */
      publish: (id) => `/country-configs/${id}/publish`,
      /** PATCH /country-configs/:id/unpublish - Unpublish country config (revert to draft) */
      unpublish: (id) => `/country-configs/${id}/unpublish`,
      /** DELETE /country-configs/:id/discard - Discard country config draft */
      discard: (id) => `/country-configs/${id}/discard`,
      /** POST /country-configs/:id/start-draft - Start new draft from published config */
      startDraft: (id) => `/country-configs/${id}/start-draft`,
      /** GET /country-configs/by-country/:countrySlug - Get country config by country slug with customization applied */
      getByCountrySlug: (countrySlug) => `/country-configs/by-country/${countrySlug}`,
      /** POST /country-configs/sync/:countrySlug - Sync country config from country-api (requires subscription) */
      sync: (countrySlug) => `/country-configs/sync/${countrySlug}`,
      /** GET /country-configs/sync/:countrySlug/preview - Preview migration for self-managed country config */
      syncPreview: (countrySlug) => `/country-configs/sync/${countrySlug}/preview`
    },
    /**
     * Permit Config endpoints
     * Self-managed permit configurations (MODE 1: SELF-MANAGED)
     */
    permitConfigs: {
      /** GET /country-config/permit-configs - List all permit configs for tenant */
      list: () => "/country-config/permit-configs",
      /** GET /country-config/permit-configs/scope/:scopeId - Get permit configs by scope */
      listByScope: (scopeId) => `/country-config/scopes/${scopeId}/permit-configs`,
      /** GET /country-config/permit-configs/:id - Get permit config by ID */
      getById: (id) => `/country-config/permit-configs/${id}`,
      /** POST /country-config/scopes/:scopeId/permit-configs - Create new permit config */
      createForScope: (scopeId) => `/country-config/scopes/${scopeId}/permit-configs`,
      /** POST /country-config/permit-configs - Create new permit config (legacy) */
      create: () => "/country-config/permit-configs",
      /** PUT /country-config/permit-configs/:id - Update permit config */
      update: (id) => `/country-config/permit-configs/${id}`,
      /** DELETE /country-config/permit-configs/:id - Delete permit config */
      delete: (id) => `/country-config/permit-configs/${id}`,
      /** GET /country-config/permit-configs/:id/caa-form - Download CAA form file */
      downloadCaaForm: (id) => `/country-config/permit-configs/${id}/caa-form`
    },
    /**
     * Country Config Scope endpoints
     * Self-managed scopes (MODE 1: SELF-MANAGED)
     * Scopes define boundaries for permit configurations within a country config
     */
    scopes: {
      /** GET /country-config/country-configs/:configId/scopes - List all scopes for a country config */
      listByCountryConfig: (configId) => `/country-config/country-configs/${configId}/scopes`,
      /** GET /country-config/scopes/:id - Get scope by ID */
      getById: (id) => `/country-config/scopes/${id}`,
      /** POST /country-config/country-configs/:configId/scopes - Create new scope */
      create: (configId) => `/country-config/country-configs/${configId}/scopes`,
      /** PUT /country-config/scopes/:id - Update scope */
      update: (id) => `/country-config/scopes/${id}`,
      /** DELETE /country-config/scopes/:id - Delete scope */
      delete: (id) => `/country-config/scopes/${id}`
    },
    /**
     * Country Config Customization endpoints
     * Organization-level customizations for country configs (MODE 1: SELF-MANAGED)
     * Allows tenants to customize country configs per organization
     */
    customizations: {
      /** GET /country-configs/:id/customizations - List all customizations for a country config */
      list: (countryConfigId) => `/country-configs/${countryConfigId}/customizations`,
      /** GET /country-configs/:id/customizations/:organizationSlug - Get customization by organization */
      getByOrganization: (countryConfigId, organizationSlug) => `/country-configs/${countryConfigId}/customizations/${organizationSlug}`,
      /** GET /country-configs/:id/with-customization - Get country config with customization applied */
      getWithCustomization: (countryConfigId) => `/country-configs/${countryConfigId}/with-customization`,
      /** POST /country-configs/:id/customizations - Create or update customization */
      upsert: (countryConfigId) => `/country-configs/${countryConfigId}/customizations`,
      /** DELETE /country-configs/:id/customizations/:organizationSlug - Delete customization */
      delete: (countryConfigId, organizationSlug) => `/country-configs/${countryConfigId}/customizations/${organizationSlug}`
    }
  },
  /**
   * Permit Config Customization endpoints
   * Organization-level customizations for permit configs (MODE 1: SELF-MANAGED)
   * Allows organizations to override permit requirements and configuration
   */
  permitConfig: {
    customizations: {
      /** GET /permit-configs/:id/customizations - List all customizations for a permit config */
      list: (permitConfigId) => `/permit-configs/${permitConfigId}/customizations`,
      /** GET /permit-configs/:id/customizations/:organizationSlug - Get customization by organization */
      getByOrganization: (permitConfigId, organizationSlug) => `/permit-configs/${permitConfigId}/customizations/${organizationSlug}`,
      /** GET /permit-configs/:id/with-customization - Get permit config with customization applied */
      getWithCustomization: (permitConfigId) => `/permit-configs/${permitConfigId}/with-customization`,
      /** POST /permit-configs/:id/customizations - Create or update customization */
      upsert: (permitConfigId) => `/permit-configs/${permitConfigId}/customizations`,
      /** DELETE /permit-configs/:id/customizations/:organizationSlug - Delete customization */
      delete: (permitConfigId, organizationSlug) => `/permit-configs/${permitConfigId}/customizations/${organizationSlug}`
    }
  },
  /**
   * Note endpoints
   * Notes are categorized annotations that can be attached to any resource
   * Levels: INFO, WARNING, DANGER
   */
  notes: {
    /** POST /notes - Create a new note */
    create: () => "/notes",
    /** GET /notes/:resourceType/:resourceId - List all notes for a specific resource */
    list: (resourceType, resourceId) => `/notes/${resourceType}/${resourceId}`,
    /** GET /notes/:id - Get a single note by ID */
    getById: (id) => `/notes/${id}`,
    /** PATCH /notes/:id - Update a note */
    update: (id) => `/notes/${id}`,
    /** DELETE /notes/:id - Delete a note */
    delete: (id) => `/notes/${id}`
  },
  /**
   * Permit endpoints
   * Permit documents attached to applications
   */
  permits: {
    /** GET /permits/:id - Get permit by ID */
    getById: (id) => `/permits/${id}`,
    /** GET /permits/application/:applicationId - Get permits for application */
    listByApplication: (applicationId) => `/permits/application/${applicationId}`,
    /** GET /permits/:permitId/resources - Get resources for permit */
    resources: (permitId) => `/permits/${permitId}/resources`,
    /** GET /permits/application/:applicationId/assignment-status - Check resource assignment status */
    assignmentStatus: (applicationId) => `/permits/application/${applicationId}/assignment-status`,
    /** POST /applications/:id/attach-permit - Attach permit to application */
    attachToApplication: (applicationId) => `/applications/${applicationId}/attach-permit`,
    /** PATCH /permits/:id - Update an existing permit */
    update: (id) => `/permits/${id}`
  },
  /**
   * Analytics endpoints
   * Event logs and activity tracking
   */
  analytics: {
    /** GET /analytics/event-logs - List all event logs */
    list: () => "/analytics/event-logs",
    /** GET /analytics/event-logs/resource/:resourceType/:resourceId - Get event logs for resource */
    eventLogs: (resourceType, resourceId) => `/analytics/event-logs/resource/${resourceType}/${resourceId}`,
    /** POST /analytics/event-logs - Create event log */
    createEventLog: () => "/analytics/event-logs",
    /** GET /analytics/custom-event-types - List all custom event types */
    customEventTypes: () => "/analytics/custom-event-types",
    /** POST /analytics/custom-event-types - Create custom event type */
    createCustomEventType: () => "/analytics/custom-event-types"
  },
  /**
   * Airports endpoints
   * Airports used by the tenant in flights/routes
   */
  airports: {
    /** GET /airports - List all airports */
    list: () => "/airports",
    /** GET /airports/:id - Get airport by ID */
    getById: (id) => `/airports/${id}`
  },
  /**
   * Application endpoints
   * Permit applications with submission tracking
   */
  applications: {
    /** GET /applications - List all applications with enhanced data */
    list: () => "/applications",
    /** GET /applications/:id - Get application by ID */
    getById: (id) => `/applications/${id}`,
    /** POST /applications - Create new application */
    create: () => "/applications",
    /** PATCH /applications/:id - Update application */
    update: (id) => `/applications/${id}`,
    /** DELETE /applications/:id - Delete application */
    delete: (id) => `/applications/${id}`,
    /** GET /applications/service-type/:serviceType/city-pair/:cityPair - Get applications by service type and city pair */
    byServiceTypeAndCityPair: (serviceType, cityPair) => `/applications/service-type/${serviceType}/city-pair/${cityPair}`,
    /** POST /applications/:id/send - Send application */
    send: (id) => `/applications/${id}/send`,
    /** GET /applications/:id/event-logs - Get event logs for application */
    eventLogs: (id) => `/applications/${id}/event-logs`,
    /** GET /applications/:id/applicable-changesets - Get applicable changesets */
    applicableChangesets: (id) => `/applications/${id}/applicable-changesets`,
    /** GET /applications/:id/resources - Get application resources */
    resources: (id) => `/applications/${id}/resources`,
    /** GET /applications/:id/resources/:resourceType - Get application resources by type */
    resourcesByType: (id, resourceType) => `/applications/${id}/resources/${resourceType}`,
    /** POST /applications/apply-for-permit - Smart apply for permit (creates app + submission + assigns changesets) */
    applyForPermit: () => "/applications/apply-for-permit",
    /** POST /applications/apply-aircrafts - Apply for aircrafts */
    applyAircrafts: () => "/applications/apply-aircrafts",
    /** POST /applications/apply-routes - Apply for routes */
    applyRoutes: () => "/applications/apply-routes",
    /** POST /applications/apply-delay - Apply for delay */
    applyDelay: () => "/applications/apply-delay",
    /** POST /applications/apply-positioning - Apply for positioning */
    applyPositioning: () => "/applications/apply-positioning",
    /** GET /applications/flights/:flightId - Get applications for a flight */
    forFlight: (flightId) => `/applications/flights/${flightId}`,
    /**
     * Application Submission endpoints
     * Nested under applications
     */
    submissions: {
      /** GET /applications/:id/submissions - List all submissions for application */
      list: (applicationId) => `/applications/${applicationId}/submissions`,
      /** GET /applications/:id/submissions/:submissionId - Get submission by ID */
      getById: (applicationId, submissionId) => `/applications/${applicationId}/submissions/${submissionId}`,
      /** POST /applications/:id/submissions - Create new submission */
      create: (applicationId) => `/applications/${applicationId}/submissions`,
      /** PATCH /applications/:id/submissions/:submissionId - Update submission */
      update: (applicationId, submissionId) => `/applications/${applicationId}/submissions/${submissionId}`,
      /** DELETE /applications/:id/submissions/:submissionId - Delete submission */
      delete: (applicationId, submissionId) => `/applications/${applicationId}/submissions/${submissionId}`,
      /** POST /applications/:id/submissions/:submissionId/prepare - Prepare submission */
      prepare: (applicationId, submissionId) => `/applications/${applicationId}/submissions/${submissionId}/prepare`,
      /** GET /applications/:id/submissions/:submissionId/applicable-changesets - Get applicable changesets for submission */
      applicableChangesets: (applicationId, submissionId) => `/applications/${applicationId}/submissions/${submissionId}/applicable-changesets`,
      /** PATCH /applications/:id/submissions/:submissionId/applicable-changesets - Update applicable changesets for submission */
      updateApplicableChangesets: (applicationId, submissionId) => `/applications/${applicationId}/submissions/${submissionId}/applicable-changesets`,
      /** POST /applications/:id/submissions/:submissionId/upload-form - Upload form file for submission (Basic plan) */
      uploadForm: (applicationId, submissionId) => `/applications/${applicationId}/submissions/${submissionId}/upload-form`,
      /** DELETE /applications/:id/submissions/:submissionId/form - Delete form file */
      deleteForm: (applicationId, submissionId) => `/applications/${applicationId}/submissions/${submissionId}/form`,
      /** PATCH /applications/:id/submissions/:submissionId/generate-previews - Generate previews for submission */
      generatePreviews: (applicationId, submissionId) => `/applications/${applicationId}/submissions/${submissionId}/generate-previews`,
      /** PATCH /applications/:id/submissions/:submissionId/sync - Sync all cached data (permit config, documents, data) for draft submission */
      sync: (applicationId, submissionId) => `/applications/${applicationId}/submissions/${submissionId}/sync`,
      /** PATCH /applications/:id/submissions/:submissionId/refresh-data - Refresh data for submission */
      refreshData: (applicationId, submissionId) => `/applications/${applicationId}/submissions/${submissionId}/refresh-data`,
      /** PATCH /applications/:id/submissions/:submissionId/refresh-documents - Refresh documents for submission */
      refreshDocuments: (applicationId, submissionId) => `/applications/${applicationId}/submissions/${submissionId}/refresh-documents`,
      /** PATCH /applications/:id/submissions/:submissionId/reject - Reject submission */
      reject: (applicationId, submissionId) => `/applications/${applicationId}/submissions/${submissionId}/reject`,
      /** POST /applications/:id/submissions/:submissionId/confirm-resources - Confirm selected resources/changesets */
      confirmResources: (applicationId, submissionId) => `/applications/${applicationId}/submissions/${submissionId}/confirm-resources`,
      /** POST /applications/:id/submissions/:submissionId/confirm-data - Confirm data review (PRO plan) */
      confirmData: (applicationId, submissionId) => `/applications/${applicationId}/submissions/${submissionId}/confirm-data`,
      /** PATCH /applications/:id/submissions/:submissionId/selector-choices - Save selector choices and regenerate data */
      saveSelectorChoices: (applicationId, submissionId) => `/applications/${applicationId}/submissions/${submissionId}/selector-choices`,
      /** POST /applications/:id/submissions/:submissionId/refresh-data-completeness - Refresh data and recalculate completeness */
      refreshDataCompleteness: (applicationId, submissionId) => `/applications/${applicationId}/submissions/${submissionId}/refresh-data-completeness`,
      /** POST /applications/:id/submissions/:submissionId/confirm-documents - Confirm documents review */
      confirmDocuments: (applicationId, submissionId) => `/applications/${applicationId}/submissions/${submissionId}/confirm-documents`,
      /** GET /applications/:id/submissions/:submissionId/event-logs - Get event logs for submission (includes Application + Submission events) */
      eventLogs: (applicationId, submissionId) => `/applications/${applicationId}/submissions/${submissionId}/event-logs`,
      /**
       * Additional Files endpoints
       * User-uploaded additional documents for a submission
       */
      additionalFiles: {
        /** GET /applications/:id/submissions/:submissionId/additional-files - List additional files */
        list: (applicationId, submissionId) => `/applications/${applicationId}/submissions/${submissionId}/additional-files`,
        /** POST /applications/:id/submissions/:submissionId/additional-files - Upload additional file */
        upload: (applicationId, submissionId) => `/applications/${applicationId}/submissions/${submissionId}/additional-files`,
        /** DELETE /applications/:id/submissions/:submissionId/additional-files/:fileId - Delete additional file */
        delete: (applicationId, submissionId, fileId) => `/applications/${applicationId}/submissions/${submissionId}/additional-files/${fileId}`
      }
    },
    /** GET /applications/:id/specific-data-configs - Get specific data configs for application */
    specificDataConfigs: (id) => `/applications/${id}/specific-data-configs`,
    /** GET /applications/:id/application-flight-groups - Get flight groups for application */
    flightGroups: (id) => `/applications/${id}/application-flight-groups`,
    /** GET /applications/:id/route-legs - Get route legs for application */
    routeLegs: (id) => `/applications/${id}/route-legs`,
    /** GET /applications/:id/city-pairs - Get city pairs for application */
    cityPairs: (id) => `/applications/${id}/city-pairs`,
    /** GET /applications/:id/application-aircrafts - Get aircrafts for application */
    applicationAircrafts: (id) => `/applications/${id}/application-aircrafts`,
    /** GET /applications/:id/route-countries - Get route countries for application */
    routeCountries: (id) => `/applications/${id}/route-countries`,
    /** GET /applications/:id/per-flight-field-values - Get per flight field values for application */
    perFlightFieldValues: (id) => `/applications/${id}/per-flight-field-values`
  },
  /**
   * Organization endpoints
   * Current tenant's organization information
   */
  organizations: {
    /** GET /organization - Get current organization */
    current: () => "/organization",
    /** POST /organization - Create new organization (current scope) */
    createCurrent: () => "/organization",
    /** PATCH /organization - Update current organization */
    updateCurrent: () => "/organization",
    /** GET /organization/all - List all organizations for tenant */
    list: () => "/organization/all",
    /** GET /organizations/:id - Get organization by ID */
    getById: (id) => `/organizations/${id}`,
    /** POST /organizations - Create new organization */
    create: () => "/organizations",
    /** PATCH /organizations/:id - Update organization */
    update: (id) => `/organizations/${id}`,
    /** DELETE /organizations/:id - Delete organization */
    delete: (id) => `/organizations/${id}`
  },
  /**
   * Tenant Profile endpoints
   * Tenant-level profile data
   */
  tenantProfile: {
    /** GET /tenant-profile - Get tenant profile */
    get: () => "/tenant-profile",
    /** POST /tenant-profile - Create tenant profile */
    create: () => "/tenant-profile",
    /** PATCH /tenant-profile/:id - Update tenant profile */
    update: (id) => `/tenant-profile/${id}`
  },
  /**
   * Aircraft endpoints
   * Fleet management
   */
  aircrafts: {
    /** GET /aircrafts - List all aircrafts */
    list: () => "/aircrafts",
    /** GET /aircrafts/:id - Get aircraft by ID */
    getById: (id) => `/aircrafts/${id}`,
    /** POST /aircrafts - Create new aircraft */
    create: () => "/aircrafts",
    /** PATCH /aircrafts/:id - Update aircraft */
    update: (id) => `/aircrafts/${id}`,
    /** DELETE /aircrafts/:id - Delete aircraft */
    delete: (id) => `/aircrafts/${id}`,
    /** GET /aircrafts/flights/:flightId - Get aircrafts for a flight */
    forFlight: (flightId) => `/aircrafts/flights/${flightId}`,
    /** GET /aircrafts/application-flight-groups/:flightGroupIds - Get aircrafts for application flight groups */
    forApplicationFlightGroups: (flightGroupIds) => `/aircrafts/application-flight-groups/${flightGroupIds}`
  },
  /**
   * User endpoints
   * User management
   */
  users: {
    /** GET /users - List all users */
    list: () => "/users",
    /** GET /users/me - Get current user */
    me: () => "/users/me",
    /** GET /users/organization - Get users for current organization */
    organization: () => "/users/organization",
    /** GET /users/:id - Get user by ID */
    getById: (id) => `/users/${id}`,
    /** POST /users - Create new user */
    create: () => "/users",
    /** PATCH /users/:id - Update user */
    update: (id) => `/users/${id}`,
    /** PATCH /users - Update current user (self) */
    updateMe: () => "/users",
    /** DELETE /users/:id - Delete user */
    delete: (id) => `/users/${id}`,
    /** POST /users/login - Login user */
    login: () => "/users/login",
    /** POST /users/:id/resend-invitation - Resend invitation email */
    resendInvitation: (id) => `/users/${id}/resend-invitation`
  },
  /**
   * Role endpoints
   * Role management (tenant-owner only for write operations)
   */
  roles: {
    /** GET /roles - List all roles with permissions and user count */
    list: () => "/roles",
    /** GET /roles/:id - Get role by ID with permissions */
    getById: (id) => `/roles/${id}`,
    /** POST /roles - Create a new role */
    create: () => "/roles",
    /** PATCH /roles/:id - Update role name/description */
    update: (id) => `/roles/${id}`,
    /** DELETE /roles/:id - Delete a role */
    delete: (id) => `/roles/${id}`,
    /** GET/PUT /roles/:id/permissions - Get or replace role permissions */
    permissions: (id) => `/roles/${id}/permissions`
  },
  /**
   * Permission endpoints
   * Read-only permission listing
   */
  permissions: {
    /** GET /permissions - List all permissions grouped by category */
    list: () => "/permissions"
  },
  /**
   * Flight endpoints
   * Flight management
   */
  flights: {
    /** GET /flights/export - Export all flights with filters */
    export: () => "/flights/export",
    /** GET /flights/:id - Get flight by ID */
    getById: (id) => `/flights/${id}`,
    /** GET /flights/:flightGroupId/flights - Get flights for a flight group */
    forFlightGroup: (flightGroupId) => `/flights/${flightGroupId}/flights`,
    /** GET /flights/:flightGroupId/application-flights - Get flights for a flight group (application context) */
    applicationFlights: (flightGroupId) => `/flights/${flightGroupId}/application-flights`,
    /** GET /flights/specific-data-configs - Get flights with specific data configs */
    specificDataConfigs: () => "/flights/specific-data-configs",
    /** GET /flights/:id/changeset - Get changeset for flight */
    changeset: (id) => `/flights/${id}/changeset`,
    /** GET /flights/:id/settings - Get settings for flight */
    settings: (id) => `/flights/${id}/settings`,
    /** POST /flights/import/apm - Import flights from APM Technologies */
    importFromApm: () => "/flights/import/apm",
    /** PATCH /flights/statuses/refresh - Refresh flight statuses */
    refreshStatuses: () => "/flights/statuses/refresh",
    /** DELETE /flights/reset - Reset flight data */
    reset: () => "/flights/reset",
    /** POST /flights/:flightId/service-type - Update service type for flight */
    updateServiceType: (flightId) => `/flights/${flightId}/service-type`,
    /** PATCH /flights/:flightId/flags - Update flight flags (isPax, isCargo, isCharter) */
    updateFlags: (flightId) => `/flights/${flightId}/flags`,
    /** PATCH /flights/:flightId/delayed - Set the manual isDelayed flag (PRD-007) */
    setDelayed: (flightId) => `/flights/${flightId}/delayed`,
    /** POST /flights/:flightId/aircrafts - Assign aircrafts to flight */
    assignAircrafts: (flightId) => `/flights/${flightId}/aircrafts`,
    /** POST /flights/:flightId/cargo-organizations - Replace cargo organizations for flight */
    replaceCargoOrganizations: (flightId) => `/flights/${flightId}/cargo-organizations`,
    /** POST /flights/:flightId/cargo-fields-data - Update cargo fields data for flight */
    updateCargoFieldsData: (flightId) => `/flights/${flightId}/cargo-fields-data`,
    /** POST /flights/:flightId/pax-fields-data - Update pax fields data for flight */
    updatePaxFieldsData: (flightId) => `/flights/${flightId}/pax-fields-data`,
    /** POST /flights/:flightId/charterers - Assign charterers to flight */
    assignCharterers: (flightId) => `/flights/${flightId}/charterers`,
    /** POST /flights/:flightId/cargo - Assign cargo info to flight */
    assignCargo: (flightId) => `/flights/${flightId}/cargo`,
    /** POST /flights/:flightId/crew-members - Assign crew members to flight */
    assignCrewMembers: (flightId) => `/flights/${flightId}/crew-members`,
    /** POST /flights/:flightId/pax - Assign pax info to flight */
    assignPax: (flightId) => `/flights/${flightId}/pax`,
    /** POST /flights/:flightId/settings - Update flight settings */
    updateSettings: (flightId) => `/flights/${flightId}/settings`,
    /** POST /flights/import - Import flight list */
    import: () => "/flights/import",
    /** GET /flights/matrix - Get matrix view data */
    matrix: () => "/flights/matrix"
  },
  /**
   * Flight Field Data endpoints
   * Per-flight field data management
   */
  flightFieldData: {
    /** POST /flight-field-data - Create flight field data */
    create: () => "/flight-field-data",
    /** PATCH /flight-field-data/:id - Update flight field data */
    update: (id) => `/flight-field-data/${id}`,
    /** GET /flight-field-data/flights/:flightId - Get field data for flight */
    forFlight: (flightId) => `/flight-field-data/flights/${flightId}`
  },
  /**
   * Changesets endpoints
   * Changeset management
   */
  changesets: {
    /** POST /changesets/batch/:id/commit - Commit batch */
    batchCommit: (id) => `/changesets/batch/${id}/commit`,
    /** POST /changesets/batch/:id/reject - Reject batch */
    batchReject: (id) => `/changesets/batch/${id}/reject`,
    /** GET /changesets/batch/:batchOperationId - Get changesets for batch operation */
    forBatch: (batchOperationId) => `/changesets/batch/${batchOperationId}`
  },
  /**
   * Tasks for flights endpoints
   * Task management for specific flights
   */
  flightTasks: {
    /** GET /tasks/flights/:flightId - Get tasks for flight */
    forFlight: (flightId) => `/tasks/flights/${flightId}`,
    /** GET /tasks/flights/:flightId/tasks - Get tasks (full Task objects) for flight */
    tasksByFlightId: (flightId) => `/tasks/flights/${flightId}/tasks`
  },
  /**
   * Submission task endpoints
   * Tasks linked to application submissions
   */
  submissionTasks: {
    /** GET /tasks/submissions/:submissionId/tasks - Get tasks for submission */
    tasksBySubmissionId: (submissionId) => `/tasks/submissions/${submissionId}/tasks`
  },
  /**
   * Flight Group endpoints
   * Flight group management
   */
  flightGroups: {
    /** GET /flight-groups - List all flight groups */
    list: () => "/flight-groups",
    /** GET /flight-groups/:id - Get flight group by ID */
    getById: (id) => `/flight-groups/${id}`,
    /** POST /flight-groups - Create new flight group */
    create: () => "/flight-groups",
    /** PATCH /flight-groups/:id - Update flight group */
    update: (id) => `/flight-groups/${id}`,
    /** DELETE /flight-groups/:id - Delete flight group */
    delete: (id) => `/flight-groups/${id}`
  },
  /**
   * Route endpoints
   * Flight route management
   */
  routes: {
    /** POST /routes/import - Import route list */
    import: () => "/routes/import",
    /** GET /routes/by-city-pair - Get routes grouped by city pair */
    byCityPair: () => "/routes/by-city-pair",
    /** GET /routes/flight/:flightId - Get routes for a flight */
    forFlight: (flightId) => `/routes/flight/${flightId}`,
    /** GET /routes/flight/:flightId/permit-configs - Get permit configs for flight routes */
    permitConfigsForFlight: (flightId) => `/routes/flight/${flightId}/permit-configs`
  },
  /**
   * Task endpoints
   * Task management
   */
  tasks: {
    /** GET /tasks - List all tasks */
    list: () => "/tasks",
    /** GET /tasks/:id - Get task by ID */
    getById: (id) => `/tasks/${id}`,
    /** POST /tasks - Create new task */
    create: () => "/tasks",
    /** PATCH /tasks/:id - Update task */
    update: (id) => `/tasks/${id}`,
    /** DELETE /tasks/:id - Delete task */
    delete: (id) => `/tasks/${id}`,
    subTasks: {
      /** POST /tasks/:taskId/sub-tasks - Create sub-task */
      create: (taskId) => `/tasks/${taskId}/sub-tasks`,
      /** PATCH /tasks/:taskId/sub-tasks/:subTaskId - Update sub-task */
      update: (taskId, subTaskId) => `/tasks/${taskId}/sub-tasks/${subTaskId}`,
      /** DELETE /tasks/:taskId/sub-tasks/:subTaskId - Delete sub-task */
      delete: (taskId, subTaskId) => `/tasks/${taskId}/sub-tasks/${subTaskId}`,
      /** PATCH /tasks/:taskId/sub-tasks/reorder - Reorder sub-tasks */
      reorder: (taskId) => `/tasks/${taskId}/sub-tasks/reorder`
    }
  },
  /**
   * Task Label endpoints
   * Manage task labels (colors, names)
   */
  taskLabels: {
    /** GET /task-labels - List all task labels */
    list: () => "/task-labels",
    /** POST /task-labels - Create task label */
    create: () => "/task-labels",
    /** PATCH /task-labels/:id - Update task label */
    update: (id) => `/task-labels/${id}`,
    /** DELETE /task-labels/:id - Delete task label */
    delete: (id) => `/task-labels/${id}`
  },
  /**
   * Label endpoints (polymorphic - shared across entity types)
   * Manage labels with resourceType filter (task, flight, etc.)
   */
  labels: {
    /** GET /labels?resourceType=task|flight - List labels filtered by resource type */
    list: () => "/labels",
    /** POST /labels - Create a label */
    create: () => "/labels",
    /** PATCH /labels/:id - Update a label */
    update: (id) => `/labels/${id}`,
    /** DELETE /labels/:id - Delete a label */
    delete: (id) => `/labels/${id}`
  },
  /**
   * Flight Label endpoints
   * Manage label assignments for flights
   */
  flightLabels: {
    /** PATCH /flights/:flightId/labels - Set labels for a flight */
    set: (flightId) => `/flights/${flightId}/labels`
  },
  /**
   * City Pair endpoints
   * City pair management for routes
   */
  cityPairs: {
    /** POST /city-pairs/:id/aircrafts - Assign aircrafts to city pair */
    assignAircrafts: (id) => `/city-pairs/${id}/aircrafts`,
    /** GET /city-pairs - List all city pairs */
    list: () => "/city-pairs",
    /** GET /city-pairs/:id - Get city pair by ID */
    getById: (id) => `/city-pairs/${id}`,
    /** GET /city-pairs/:id/aircraft-assignments - Get aircraft assignments for city pair */
    aircraftAssignments: (id) => `/city-pairs/${id}/aircraft-assignments`,
    /** GET /city-pairs/all/:ids/aircrafts - Get aircrafts for multiple city pairs */
    aircraftsForMultiple: (ids) => `/city-pairs/all/${ids}/aircrafts`,
    /** POST /city-pairs - Create new city pair */
    create: () => "/city-pairs",
    /** PATCH /city-pairs/:id - Update city pair */
    update: (id) => `/city-pairs/${id}`,
    /** DELETE /city-pairs/:id - Delete city pair */
    delete: (id) => `/city-pairs/${id}`
  },
  /**
   * Specific Data endpoints
   * Flight-specific data management
   */
  specificData: {
    /** POST /specific-data - Create specific data */
    create: () => "/specific-data",
    /** GET /specific-data/documents - List all specific data documents */
    documents: () => "/specific-data/documents",
    /** DELETE /specific-data/documents/:id - Delete specific data document */
    deleteDocument: (id) => `/specific-data/documents/${id}`,
    /** GET /specific-data/fields - List all specific data fields */
    fields: () => "/specific-data/fields",
    /** DELETE /specific-data/fields/:id - Delete specific data field */
    deleteField: (id) => `/specific-data/fields/${id}`,
    /** GET /specific-data/flights/:flightId/config - Get specific data config for flight */
    flightConfig: (flightId) => `/specific-data/flights/${flightId}/config`
  },
  /**
   * Flight Documents endpoints
   * Per-flight document management
   */
  flightDocuments: {
    /** GET /flight-documents/flights/:flightId - Get documents for flight */
    forFlight: (flightId) => `/flight-documents/flights/${flightId}`,
    /** PATCH /flight-documents/flights/:flightId - Update documents for flight */
    updateForFlight: (flightId) => `/flight-documents/flights/${flightId}`
  },
  /**
   * Documents endpoints
   * Resource document management
   */
  documents: {
    /** GET /documents - List all documents */
    list: () => "/documents",
    /** POST /documents - Upload a document */
    create: () => "/documents",
    /** GET /documents/resource/:resourceType/:resourceId - Get documents for resource */
    forResource: (resourceType, resourceId) => `/documents/resource/${resourceType}/${resourceId}`,
    /** GET /documents/resource/:resourceType/:resourceId/configs/:documentConfigId/versions - Get document versions */
    versions: (resourceType, resourceId, documentConfigId) => `/documents/resource/${resourceType}/${resourceId}/configs/${documentConfigId}/versions`
  },
  /**
   * Document Assignments endpoints
   * Document assignment management (linking documents to resources)
   */
  documentAssignments: {
    /** DELETE /document-assignments/:id - Delete document assignment */
    delete: (id) => `/document-assignments/${id}`
  },
  /**
   * Crew endpoints
   * Crew member management
   */
  crew: {
    /** GET /crew - List all crew members */
    list: () => "/crew",
    /** GET /crew/type/:type - List crew members by type */
    listByType: (type) => `/crew/type/${type}`,
    /** GET /crew/icao-types/:icaoTypeId - List crew members by ICAO type */
    listByIcaoType: (icaoTypeId) => `/crew/icao-types/${icaoTypeId}`,
    /** GET /crew/:id - Get crew member by ID */
    getById: (id) => `/crew/${id}`,
    /** POST /crew - Create new crew member */
    create: () => "/crew",
    /** PATCH /crew/:id - Update crew member */
    update: (id) => `/crew/${id}`,
    /** DELETE /crew/:id - Delete crew member */
    delete: (id) => `/crew/${id}`
  },
  /**
   * ICAO Types endpoints
   * ICAO aircraft type management
   */
  icaoTypes: {
    /** GET /icao-types - List all ICAO types */
    list: () => "/icao-types",
    /** GET /icao-types/:id - Get ICAO type by ID */
    getById: (id) => `/icao-types/${id}`,
    /** POST /icao-types - Create new ICAO type */
    create: () => "/icao-types",
    /** PATCH /icao-types/:id - Update ICAO type */
    update: (id) => `/icao-types/${id}`,
    /** DELETE /icao-types/:id - Delete ICAO type */
    delete: (id) => `/icao-types/${id}`
  },
  /**
   * Charterers endpoints
   * Charterer management
   */
  charterers: {
    /** GET /charterers - List all charterers */
    list: () => "/charterers",
    /** GET /charterers/:id - Get charterer by ID */
    getById: (id) => `/charterers/${id}`,
    /** POST /charterers - Create new charterer */
    create: () => "/charterers",
    /** PATCH /charterers/:id - Update charterer */
    update: (id) => `/charterers/${id}`,
    /** DELETE /charterers/:id - Delete charterer */
    delete: (id) => `/charterers/${id}`
  },
  /**
   * Cargo endpoints
   * Cargo and cargo organization management
   */
  cargo: {
    /** GET /cargo - Get cargo details */
    details: () => "/cargo",
    /** PUT /cargo - Save cargo details */
    save: () => "/cargo",
    /** GET /cargo/organizations - List all cargo organizations */
    organizations: () => "/cargo/organizations",
    /** GET /cargo/organizations/:id - Get cargo organization by ID */
    organizationById: (id) => `/cargo/organizations/${id}`,
    /** POST /cargo/organizations - Create new cargo organization */
    createOrganization: () => "/cargo/organizations",
    /** PATCH /cargo/organizations/:id - Update cargo organization */
    updateOrganization: (id) => `/cargo/organizations/${id}`,
    /** DELETE /cargo/organizations/:id - Delete cargo organization */
    deleteOrganization: (id) => `/cargo/organizations/${id}`
  },
  /**
   * Pax endpoints
   * Passenger management
   */
  pax: {
    /** GET /pax - Get passenger details */
    details: () => "/pax",
    /** PUT /pax - Save passenger details */
    save: () => "/pax",
    /** GET /pax/:id - Get passenger by ID */
    getById: (id) => `/pax/${id}`
  },
  /**
   * Station Managers endpoints
   * Station manager management
   */
  stationManagers: {
    /** GET /station-managers - List all station managers */
    list: () => "/station-managers",
    /** GET /station-managers/:id - Get station manager by ID */
    getById: (id) => `/station-managers/${id}`,
    /** GET /station-managers/:id/countries - Get countries for station manager */
    countries: (id) => `/station-managers/${id}/countries`,
    /** GET /station-managers/:stationManagerId/countries/:countryId - Get country by ID */
    countryById: (stationManagerId, countryId) => `/station-managers/${stationManagerId}/countries/${countryId}`,
    /** POST /station-managers/:id/countries - Create country for station manager */
    createCountry: (id) => `/station-managers/${id}/countries`,
    /** PATCH /station-managers/:stationManagerId/countries/:countryId - Update country */
    updateCountry: (stationManagerId, countryId) => `/station-managers/${stationManagerId}/countries/${countryId}`,
    /** DELETE /station-managers/:stationManagerId/countries/:countryId - Delete country */
    deleteCountry: (stationManagerId, countryId) => `/station-managers/${stationManagerId}/countries/${countryId}`,
    /** GET /station-managers/:stationManagerId/countries/:countryId/airports - Get airports for country */
    countryAirports: (stationManagerId, countryId) => `/station-managers/${stationManagerId}/countries/${countryId}/airports`,
    /** POST /station-managers/:stationManagerId/countries/:countryId/airports - Create airport for country */
    createCountryAirport: (stationManagerId, countryId) => `/station-managers/${stationManagerId}/countries/${countryId}/airports`,
    /** DELETE /station-managers/:stationManagerId/countries/:countryId/airports/:airportId - Delete airport */
    deleteCountryAirport: (stationManagerId, countryId, airportId) => `/station-managers/${stationManagerId}/countries/${countryId}/airports/${airportId}`,
    /** GET /station-managers/:stationManagerId/countries/:countryId/airports/:airportId - Get airport by ID */
    countryAirportById: (stationManagerId, countryId, airportId) => `/station-managers/${stationManagerId}/countries/${countryId}/airports/${airportId}`,
    /** POST /station-managers - Create new station manager */
    create: () => "/station-managers",
    /** PATCH /station-managers/:id - Update station manager */
    update: (id) => `/station-managers/${id}`,
    /** DELETE /station-managers/:id - Delete station manager */
    delete: (id) => `/station-managers/${id}`
  },
  /**
   * Settings endpoints
   * Tenant settings management
   */
  settings: {
    /** GET /settings - Get tenant settings */
    get: () => "/settings",
    /** PATCH /settings - Update tenant settings */
    update: () => "/settings"
  },
  /**
   * Batch Operations endpoints
   * Bulk import/export operations
   */
  batchOperations: {
    /** GET /batch-operations - List all batch operations */
    list: () => "/batch-operations",
    /** GET /batch-operations/:id - Get batch operation by ID */
    getById: (id) => `/batch-operations/${id}`,
    /** POST /batch-operations - Create new batch operation */
    create: () => "/batch-operations",
    /** POST /batch-operations/:id/retry - Retry a failed batch operation */
    retry: (id) => `/batch-operations/${id}/retry`,
    /** POST /batch-operations/:id/discard - Discard a failed/pending batch operation */
    discard: (id) => `/batch-operations/${id}/discard`,
    /** GET /batch-operations/processing-status/sse - SSE stream for processing lock status */
    processingStatusSse: () => "/batch-operations/processing-status/sse",
    /** GET /batch-operations/:id/sse - SSE stream for real-time batch operation updates */
    sse: (id) => `/batch-operations/${id}/sse`
  },
  /**
   * Comments endpoints
   * Resource comments management
   */
  comments: {
    /** GET /comments/:resourceType/:resourceId - Get comments for resource */
    forResource: (resourceType, resourceId) => `/comments/${resourceType}/${resourceId}`,
    /** POST /comments - Create new comment */
    create: () => "/comments",
    /** DELETE /comments/:id - Delete comment */
    delete: (id) => `/comments/${id}`
  },
  /**
   * Service Providers endpoints
   * Service provider management
   */
  serviceProviders: {
    /** GET /service-providers - List all service providers */
    list: () => "/service-providers",
    /** GET /service-providers/airport/:airportId - Get service providers for airport */
    forAirport: (airportId) => `/service-providers/airport/${airportId}`,
    /** POST /service-providers - Create new service provider */
    create: () => "/service-providers"
  },
  /**
   * Permit Configs (tenant-level) endpoints
   * Different from countryConfig.permitConfigs - these are direct permit config queries
   */
  permitConfigs: {
    /** GET /permit-configs/:countrySlug - Get permit configs for country */
    listByCountry: (countrySlug) => `/permit-configs/${countrySlug}`,
    /** GET /permit-configs/name/:name - Get permit config by name */
    getByName: (name) => `/permit-configs/name/${name}`,
    /** PATCH /permit-configs/name/:name - Update permit config by name */
    updateByName: (name) => `/permit-configs/name/${name}`
  },
  /**
   * Service Type Settings endpoints
   * Service type configuration management
   */
  serviceTypeSettings: {
    /** GET /service-type-settings - List all service type settings */
    list: () => "/service-type-settings",
    /** POST /service-type-settings - Create or update service type settings */
    upsert: () => "/service-type-settings"
  },
  /**
   * Default Data Settings endpoints
   * Centralized default data configuration
   */
  defaultDataSettings: {
    /** GET /default-data-settings - Get default data settings */
    get: () => "/default-data-settings",
    /** POST /default-data-settings - Create or update default data settings */
    upsert: () => "/default-data-settings"
  },
  /**
   * Analytics endpoints (dashboard)
   * Dashboard data and analytics
   */
  dashboard: {
    /** GET /analytics/dashboard - Get dashboard analytics data */
    get: () => "/analytics/dashboard"
  },
  /**
   * Overflight Countries endpoints
   * Overflight country management for flight permits
   */
  overflightCountries: {
    /** GET /overflight-countries/:country/:serviceType - Get overflight country info */
    getInfo: (country, serviceType) => `/overflight-countries/${country}/${serviceType}`,
    /** GET /overflight-countries/:country/:serviceType/flights - Get flights for overflight country */
    flights: (country, serviceType) => `/overflight-countries/${country}/${serviceType}/flights`
  },
  /**
   * Destinations endpoints
   * Destination management for flight permits
   */
  destinations: {
    /** GET /destinations/:country/:airportName/:serviceType - Get destination info */
    getInfo: (country, airportName, serviceType) => `/destinations/${country}/${airportName}/${serviceType}`,
    /** GET /destinations/:country/:airportName/:serviceType/flights - Get flights for destination */
    flights: (country, airportName, serviceType) => `/destinations/${country}/${airportName}/${serviceType}/flights`
  },
  /**
   * Applicable Changesets endpoints
   * Changeset management for flight route items
   */
  applicableChangesets: {
    /** GET /applicable-changesets/flight-route-items/:flightRouteItemId - Get applicable changesets for flight route item */
    forFlightRouteItem: (flightRouteItemId) => `/applicable-changesets/flight-route-items/${flightRouteItemId}`
  },
  /**
   * Completed Flights endpoints
   * Completed flight operations
   */
  completedFlights: {
    /** GET /flights/completed - List all completed flights */
    list: () => "/flights/completed"
  },
  /**
   * Aircraft Document Check endpoints
   * Document expiration checking for aircrafts
   */
  aircraftDocumentCheck: {
    /** GET /aircrafts/flight-route-items/:flightRouteItemId/check-documents - Check documents for aircrafts in flight route item */
    checkDocuments: (flightRouteItemId) => `/aircrafts/flight-route-items/${flightRouteItemId}/check-documents`
  },
  /**
   * Integrations endpoints
   * Third-party service integrations (OAuth, API keys)
   */
  integrations: {
    /** GET /integrations/categories - List all integration categories with available integrations */
    categories: () => "/integrations/categories",
    /** GET /integrations - List tenant integrations (enabled status, no credentials) */
    list: () => "/integrations",
    /** GET /integrations/:integrationId - Get tenant integration config (masked secrets) */
    getById: (integrationId) => `/integrations/${integrationId}`,
    /** POST /integrations/:integrationId - Create or update tenant integration */
    upsert: (integrationId) => `/integrations/${integrationId}`,
    /** POST /integrations/:integrationId/test - Test integration connection */
    test: (integrationId) => `/integrations/${integrationId}/test`,
    /** DELETE /integrations/:integrationId - Delete tenant integration */
    delete: (integrationId) => `/integrations/${integrationId}`
  },
  /**
   * Notifications endpoints
   * In-app notifications with SSE real-time streaming
   */
  notifications: {
    /** GET /notifications/users/:userId - List all notifications for user */
    listByUser: (userId) => `/notifications/users/${userId}`,
    /** GET /notifications/:id - Get single notification */
    getById: (id) => `/notifications/${id}`,
    /** PATCH /notifications/:id/read - Mark notification as read */
    markAsRead: (id) => `/notifications/${id}/read`,
    /** PATCH /notifications/read-all - Mark all notifications as read */
    markAllAsRead: () => "/notifications/read-all",
    /** DELETE /notifications/:id - Delete notification */
    delete: (id) => `/notifications/${id}`,
    /** GET /notifications/sse/:userId - SSE stream for real-time notifications */
    sse: (userId) => `/notifications/sse/${userId}`
  },
  /**
   * Data Completeness endpoints
   */
  dataCompleteness: {
    /** GET /data-completeness/summary - Get cached completeness snapshot */
    summary: () => "/data-completeness/summary",
    /** GET /data-completeness/details - Get detailed per-entity breakdown (computed on demand) */
    details: (permitConfigName, submissionId) => {
      const params = new URLSearchParams();
      if (permitConfigName) params.set("permitConfigName", permitConfigName);
      if (submissionId) params.set("submissionId", submissionId);
      const qs = params.toString();
      return qs ? `/data-completeness/details?${qs}` : "/data-completeness/details";
    },
    /** POST /data-completeness/refresh - Recompute snapshot */
    refresh: () => "/data-completeness/refresh"
  }
};

// src/tenant-api/dto/create-fir.schema.ts
import { z } from "zod";
var createTenantFirFieldSchemas = {
  /** Country config ID this FIR belongs to */
  countryConfigId: z.string().cuid(),
  /** ICAO code for the FIR (4 letters), e.g., 'EPWW' for Warsaw FIR */
  icaoCode: fieldSchemas.icaoCode,
  /** Full name of the FIR, e.g., 'Warsaw FIR' */
  name: z.string().min(1).max(255)
};
var createTenantFirSchema = z.object(createTenantFirFieldSchemas);

// src/tenant-api/dto/update-fir.schema.ts
import { z as z2 } from "zod";
var updateTenantFirFieldSchemas = {
  /** ICAO code for the FIR */
  icaoCode: fieldSchemas.icaoCode.optional(),
  /** Full name of the FIR */
  name: z2.string().min(1).max(255).optional()
};
var updateTenantFirSchema = z2.object(updateTenantFirFieldSchemas);

// src/tenant-api/dto/create-airport.schema.ts
import { z as z3 } from "zod";
var createTenantAirportFieldSchemas = {
  /** Country config ID this airport belongs to */
  countryConfigId: z3.string().cuid(),
  /** FIR ID this airport belongs to */
  countryConfigFirId: z3.string().cuid(),
  /** ICAO code for the airport (4 letters), e.g., 'EPWA' for Warsaw Chopin */
  icaoCode: fieldSchemas.icaoCode,
  /** IATA code for the airport (3 letters), e.g., 'WAW' for Warsaw */
  iataCode: fieldSchemas.iataCode.optional(),
  /** Full name of the airport, e.g., 'Warsaw Chopin Airport' */
  name: z3.string().min(1).max(255),
  /** City where the airport is located */
  city: z3.string().min(1).max(255).optional(),
  /** Optional notes about the airport */
  notes: z3.string().optional()
};
var createTenantAirportSchema = z3.object(
  createTenantAirportFieldSchemas
);

// src/tenant-api/dto/update-airport.schema.ts
import { z as z4 } from "zod";
var updateTenantAirportFieldSchemas = {
  /** ICAO code */
  icaoCode: fieldSchemas.icaoCode.optional(),
  /** IATA code */
  iataCode: fieldSchemas.iataCode.optional(),
  /** Airport name */
  name: z4.string().min(1).max(255).optional(),
  /** City */
  city: z4.string().min(1).max(255).optional(),
  /** Notes */
  notes: z4.string().optional()
};
var updateTenantAirportSchema = z4.object(
  updateTenantAirportFieldSchemas
);

// src/tenant-api/dto/upsert-country-config-customization.schema.ts
import { z as z5 } from "zod";
var upsertCountryConfigCustomizationFieldSchemas = {
  /** Organization slug for this customization */
  organizationSlug: z5.string().min(1).max(255),
  /** Configuration overrides (deep-merged with base config) */
  config: countryConfigScopeConfigSchema.optional(),
  /** Additional organization-specific metadata (NOT merged with base) */
  data: z5.record(z5.string(), z5.any()).optional()
};
var upsertCountryConfigCustomizationSchema = z5.object(
  upsertCountryConfigCustomizationFieldSchemas
);

// src/tenant-api/dto/upsert-permit-config-customization.schema.ts
import { z as z6 } from "zod";
var upsertPermitConfigCustomizationFieldSchemas = {
  /** Organization slug for this customization (optional, defaults to scope context from x-organization-slug header) */
  organizationSlug: z6.string().min(1).max(255).optional(),
  /** Whether this permit is required for the organization (overrides base config) */
  required: z6.boolean().optional(),
  /** Configuration overrides (deep-merged with base config) */
  config: permitConfigConfigSchema.optional(),
  /** Additional organization-specific metadata (NOT merged with base) */
  data: z6.record(z6.string(), z6.any()).optional()
};
var upsertPermitConfigCustomizationSchema = z6.object(
  upsertPermitConfigCustomizationFieldSchemas
);

// src/tenant-api/dto/create-note.schema.ts
import { z as z7 } from "zod";

// src/tenant-api/enums/index.ts
var CountryConfigSource = /* @__PURE__ */ ((CountryConfigSource2) => {
  CountryConfigSource2["SELF_MANAGED"] = "self-managed";
  CountryConfigSource2["SYNCED"] = "synced";
  return CountryConfigSource2;
})(CountryConfigSource || {});
var PermitConfigSource = /* @__PURE__ */ ((PermitConfigSource2) => {
  PermitConfigSource2["SYNCED"] = "synced";
  PermitConfigSource2["CUSTOM"] = "custom";
  return PermitConfigSource2;
})(PermitConfigSource || {});
var NoteLevel = /* @__PURE__ */ ((NoteLevel2) => {
  NoteLevel2["INFO"] = "INFO";
  NoteLevel2["WARNING"] = "WARNING";
  NoteLevel2["DANGER"] = "DANGER";
  return NoteLevel2;
})(NoteLevel || {});
var ApplicationSubmissionStatus = /* @__PURE__ */ ((ApplicationSubmissionStatus2) => {
  ApplicationSubmissionStatus2["DRAFT"] = "DRAFT";
  ApplicationSubmissionStatus2["SENT"] = "SENT";
  ApplicationSubmissionStatus2["APPROVED"] = "APPROVED";
  ApplicationSubmissionStatus2["REJECTED"] = "REJECTED";
  return ApplicationSubmissionStatus2;
})(ApplicationSubmissionStatus || {});
var ApplicationSubmissionType = /* @__PURE__ */ ((ApplicationSubmissionType2) => {
  ApplicationSubmissionType2["INITIAL"] = "INITIAL";
  ApplicationSubmissionType2["CORRECTION"] = "CORRECTION";
  ApplicationSubmissionType2["DELAY"] = "DELAY";
  ApplicationSubmissionType2["POSITIONING"] = "POSITIONING";
  ApplicationSubmissionType2["ROUTE"] = "ROUTE";
  ApplicationSubmissionType2["AIRCRAFT"] = "AIRCRAFT";
  return ApplicationSubmissionType2;
})(ApplicationSubmissionType || {});

// src/tenant-api/dto/create-note.schema.ts
var createNoteFieldSchemas = {
  text: z7.string().min(1, "Note text is required").max(5e3, "Note text must be less than 5000 characters"),
  resourceId: z7.string().min(1, "Resource ID is required"),
  resourceType: z7.string().min(1, "Resource type is required"),
  level: z7.enum(NoteLevel)
};
var createNoteSchema = z7.object(createNoteFieldSchemas);

// src/tenant-api/dto/update-note.schema.ts
var updateNoteSchema = createNoteSchema.partial();

// src/tenant-api/dto/upsert-tenant-integration.schema.ts
import { z as z8 } from "zod";
var upsertTenantIntegrationFieldSchemas = {
  /** Whether the integration is enabled */
  enabled: z8.boolean(),
  /** Integration configuration (varies by integration type) */
  config: z8.record(z8.string(), z8.any()).optional()
};
var upsertTenantIntegrationSchema = z8.object(
  upsertTenantIntegrationFieldSchemas
);
var apmConfigFieldSchemas = {
  /** OAuth Client ID from APM */
  clientId: z8.string().min(1, "Client ID is required"),
  /** OAuth Client Secret from APM */
  clientSecret: z8.string().min(1, "Client Secret is required"),
  /** APM API base URL */
  baseUrl: z8.string().url("Base URL must be a valid URL")
};
var apmConfigSchema = z8.object(apmConfigFieldSchemas);
var smtpConfigFieldSchemas = {
  /** SMTP server hostname */
  host: z8.string().min(1, "SMTP Host is required"),
  /** SMTP server port */
  port: z8.coerce.number().int().min(1).max(65535).default(587),
  /** Enable TLS encryption */
  useTls: z8.boolean().default(true),
  /** SMTP authentication username (email address) */
  username: fieldSchemas.optionalEmail,
  /** SMTP authentication password */
  password: z8.string().optional().default(""),
  /** Email address used as the sender */
  fromAddress: fieldSchemas.requiredEmail,
  /** Default recipient email address */
  toAddress: fieldSchemas.optionalEmail
};
var smtpConfigSchema = z8.object(smtpConfigFieldSchemas);

// src/tenant-api/dto/create-organization.schema.ts
import { z as z9 } from "zod";
var createOrganizationFieldSchemas = {
  /** Organization type (defaults to AIRLINE) */
  type: z9.nativeEnum(OrganizationType).optional().default("AIRLINE" /* AIRLINE */),
  /** Organization name */
  name: z9.string().min(1),
  /** Airline ICAO designator (3 letters, e.g. "MAA", "GTR"). */
  icaoCode: fieldSchemas.airlineIcaoCode,
  /** Airline IATA designator (2 alphanumeric chars, e.g. "M7", "8S"). */
  iataCode: fieldSchemas.airlineIataCode,
  /** Country slug (ISO 2-letter lowercase) */
  country: fieldSchemas.countrySlug,
  /** Freight types */
  freightTypes: z9.array(z9.string()),
  /** Number of designations for air operator */
  numberOfDesignationsForAirOperator: z9.string().optional(),
  /** Head office address */
  headOfficeAddress: z9.any().optional(),
  /** Registered address */
  registeredAddress: z9.any().optional(),
  /** Mailing address */
  mailingAddress: z9.any().optional(),
  /** Contact information */
  contactInfo: z9.any().optional(),
  /** Invoice information */
  invoiceInfo: z9.any().optional(),
  /** Codeshare information */
  codeshareInfo: z9.any().optional(),
  /** Documents */
  documents: z9.array(
    z9.object({
      documentConfigId: z9.string(),
      documentId: z9.string()
    })
  ).optional().default([])
};
var createOrganizationSchema = z9.object(
  createOrganizationFieldSchemas
);

// src/tenant-api/dto/update-organization.schema.ts
var updateOrganizationSchema = createOrganizationSchema.omit({ type: true }).partial();

// src/tenant-api/dto/create-aircraft.schema.ts
import { z as z10 } from "zod";
var createAircraftFieldSchemas = {
  /** ICAO type ID */
  icaoTypeId: z10.string().min(1),
  /** Freight types */
  freightTypes: z10.array(z10.string()),
  /** Status */
  status: z10.string().min(1),
  /** Aircraft serial number */
  acSerialNumber: z10.string().min(1),
  /** Registration */
  registration: z10.string().min(1),
  /** Registration country */
  registrationCountry: z10.string().min(1),
  /** MTOW value */
  mtowValue: z10.number().nullable().optional(),
  /** MTOW unit */
  mtowUnit: z10.string().optional(),
  /** TCAS/ACAS */
  tcasAcas: z10.boolean().optional(),
  /** Wake turbulence category */
  wtc: z10.string().optional(),
  /** Radio frequencies */
  radioFrequencies: z10.string().optional(),
  /** Range value */
  rangeValue: z10.number().nullable().optional(),
  /** Range unit */
  rangeUnit: z10.string().optional(),
  /** Manufacture date */
  manufactureDate: z10.preprocess(
    (val) => val === "" || val === null || val === void 0 ? void 0 : val,
    z10.coerce.date().optional()
  ).optional(),
  /** Owner information */
  ownerInfo: z10.any().optional(),
  /** Capacity information */
  capacityInfo: z10.any().optional(),
  /** Crew information */
  crewInfo: z10.any().optional(),
  /** Max load unit */
  maxLoadUnit: z10.string().optional(),
  /** Max load value */
  maxLoadValue: z10.number().nullable().optional()
};
var createAircraftSchema = z10.object(createAircraftFieldSchemas);

// src/tenant-api/dto/update-aircraft.schema.ts
var updateAircraftSchema = createAircraftSchema.partial();

// src/tenant-api/dto/create-crew.schema.ts
import { z as z11 } from "zod";
var createCrewFieldSchemas = {
  /** Crew type */
  type: z11.string().min(1),
  /** First name */
  firstName: z11.string().min(1),
  /** Last name */
  lastName: z11.string().min(1),
  /** Position */
  position: z11.string().optional(),
  /** Nationality */
  nationality: z11.string().optional(),
  /** Birth date */
  birthDate: z11.coerce.date(),
  /** Passport number */
  passportNumber: z11.string().min(1),
  /** Passport expiry date */
  passportExpiryDate: z11.coerce.date(),
  /** License data */
  licenseData: z11.string().min(1),
  /** English level */
  englishLevel: z11.string().min(1),
  /** Visa information */
  visaInfo: z11.any().optional(),
  /** ICAO types */
  icaoTypes: z11.array(z11.string())
};
var createCrewSchema = z11.object(createCrewFieldSchemas);

// src/tenant-api/dto/update-crew.schema.ts
import { z as z12 } from "zod";
var updateCrewFieldSchemas = {
  /** First name */
  firstName: z12.string().min(1),
  /** Last name */
  lastName: z12.string().min(1),
  /** Position */
  position: z12.string().optional(),
  /** Nationality */
  nationality: z12.string().optional(),
  /** Birth date */
  birthDate: z12.coerce.date(),
  /** Passport number */
  passportNumber: z12.string().min(1),
  /** Passport expiry date */
  passportExpiryDate: z12.coerce.date(),
  /** License data */
  licenseData: z12.string().min(1),
  /** English level */
  englishLevel: z12.string().min(1),
  /** Visa information */
  visaInfo: z12.any().optional(),
  /** ICAO types */
  icaoTypes: z12.array(z12.string())
};
var updateCrewSchema = z12.object(updateCrewFieldSchemas);

// src/tenant-api/dto/create-charterer.schema.ts
import { z as z13 } from "zod";
var createChartererFieldSchemas = {
  /** Company name */
  companyName: z13.string().min(1),
  /** Address information */
  addressInfo: z13.any().optional(),
  /** Contact person information */
  contactPersonInfo: z13.any().optional()
};
var createChartererSchema = z13.object(createChartererFieldSchemas);

// src/tenant-api/dto/update-charterer.schema.ts
import { z as z14 } from "zod";
var updateChartererSchema = z14.object(createChartererFieldSchemas);

// src/tenant-api/dto/create-task.schema.ts
import { z as z15 } from "zod";
var taskTypeEnum = z15.enum([
  "GENERIC",
  "FLIGHT_SETTINGS",
  "SEND_FLIGHT_INITIAL_APPLICATION",
  "SEND_FLIGHT_CORRECTION_APPLICATION",
  "SEND_FLIGHT_DELAY_APPLICATION",
  "SEND_FLIGHT_POSITIONING_APPLICATION",
  "SEND_ROUTE_APPLICATION",
  "SEND_AIRCRAFT_APPLICATION"
]);
var APPLICATION_TASK_TYPES = [
  "SEND_FLIGHT_INITIAL_APPLICATION",
  "SEND_FLIGHT_CORRECTION_APPLICATION",
  "SEND_AIRCRAFT_APPLICATION",
  "SEND_ROUTE_APPLICATION",
  "SEND_FLIGHT_DELAY_APPLICATION",
  "SEND_FLIGHT_POSITIONING_APPLICATION"
];
var subTaskSchema = z15.object({
  customName: z15.string().optional(),
  systemName: z15.string().optional(),
  config: z15.any()
});
var specificDataSchema = z15.object({
  flightIds: z15.array(z15.string()).optional(),
  subTasks: z15.array(subTaskSchema).optional(),
  flightId: z15.string().optional(),
  applicationId: z15.string().optional(),
  applicationSubmissionId: z15.string().optional(),
  permitConfigName: z15.string().optional(),
  // Delay-specific
  newStd: z15.string().optional(),
  // Positioning-specific
  serviceType: z15.string().optional(),
  flightNumber: z15.string().optional(),
  callSign: z15.string().optional(),
  destination: z15.string().optional(),
  std: z15.string().optional(),
  sta: z15.string().optional(),
  reason: z15.string().optional(),
  arrivalFlightId: z15.string().optional()
});
var createTaskFieldSchemas = {
  /** Task title */
  title: z15.string().min(1),
  /** Task type */
  type: taskTypeEnum,
  /** Task description */
  description: z15.string().optional(),
  /** Role ID */
  roleId: z15.string().optional(),
  /** User ID */
  userId: z15.string().optional(),
  /** Deadline */
  deadline: z15.string().optional(),
  /** Parent task ID */
  taskId: z15.string().optional(),
  /** Specific data */
  data: specificDataSchema
};
var createTaskSchema = z15.object(createTaskFieldSchemas).superRefine((data, ctx) => {
  const type = data.type;
  const taskData = data.data;
  const FLIGHT_REQUIRED_TYPES = [
    "SEND_AIRCRAFT_APPLICATION",
    "SEND_ROUTE_APPLICATION",
    "SEND_FLIGHT_DELAY_APPLICATION",
    "SEND_FLIGHT_POSITIONING_APPLICATION"
  ];
  if (FLIGHT_REQUIRED_TYPES.includes(type) && !taskData.flightId) {
    ctx.addIssue({
      code: z15.ZodIssueCode.custom,
      message: "flightId is required for this task type",
      path: ["data", "flightId"]
    });
  }
  if (type === "SEND_FLIGHT_DELAY_APPLICATION" && !taskData.newStd) {
    ctx.addIssue({
      code: z15.ZodIssueCode.custom,
      message: "newStd is required for delay application tasks",
      path: ["data", "newStd"]
    });
  }
  if (isApplicationTaskType(type) && !taskData.permitConfigName) {
    ctx.addIssue({
      code: z15.ZodIssueCode.custom,
      message: "permitConfigName is required for application task types",
      path: ["data", "permitConfigName"]
    });
  }
});
function isApplicationTaskType(type) {
  return APPLICATION_TASK_TYPES.includes(type);
}

// src/tenant-api/dto/update-task.schema.ts
import { z as z16 } from "zod";
var updateTaskFieldSchemas = {
  /** Task title */
  title: z16.string().min(1),
  /** Task status */
  status: z16.number(),
  /** Task description */
  description: z16.string(),
  /** User ID */
  userId: z16.string().optional(),
  /** Deadline */
  deadline: z16.string(),
  /** Task data */
  data: z16.any(),
  /** Label IDs to assign */
  labelIds: z16.array(z16.string())
};
var updateTaskSchema = z16.object(updateTaskFieldSchemas).partial();

// src/tenant-api/dto/create-sub-task.schema.ts
import { z as z17 } from "zod";
var createSubTaskFieldSchemas = {
  title: z17.string().min(1),
  description: z17.string().optional()
};
var createSubTaskSchema = z17.object(createSubTaskFieldSchemas);

// src/tenant-api/dto/update-sub-task.schema.ts
import { z as z18 } from "zod";
var updateSubTaskFieldSchemas = {
  title: z18.string().min(1),
  status: z18.number().int().min(0).max(2)
};
var updateSubTaskSchema = z18.object(updateSubTaskFieldSchemas).partial();

// src/tenant-api/dto/reorder-sub-tasks.schema.ts
import { z as z19 } from "zod";
var reorderSubTasksSchema = z19.object({
  orderedIds: z19.array(z19.string().min(1)).min(1)
});

// src/tenant-api/dto/create-task-label.schema.ts
import { z as z20 } from "zod";
var labelResourceTypes = ["task", "flight"];
var createTaskLabelFieldSchemas = {
  name: z20.string().min(1).max(50),
  color: z20.string().regex(/^#[0-9a-fA-F]{6}$/),
  resourceType: z20.enum(labelResourceTypes).optional().default("task")
};
var createTaskLabelSchema = z20.object(createTaskLabelFieldSchemas);

// src/tenant-api/dto/update-task-label.schema.ts
import { z as z21 } from "zod";
var updateTaskLabelFieldSchemas = {
  name: z21.string().min(1).max(50),
  color: z21.string().regex(/^#[0-9a-fA-F]{6}$/)
};
var updateTaskLabelSchema = z21.object(updateTaskLabelFieldSchemas).partial();

// src/tenant-api/dto/set-flight-labels.schema.ts
import { z as z22 } from "zod";
var setFlightLabelsSchema = z22.object({
  labelIds: z22.array(z22.string().min(1)).max(50)
});

// src/tenant-api/dto/upsert-cargo.schema.ts
import { z as z23 } from "zod";
var upsertCargoFieldSchemas = {
  /** Cargo ID (optional for create) */
  id: z23.string().optional(),
  /** Default values */
  defaultValues: z23.any()
};
var upsertCargoSchema = z23.object(upsertCargoFieldSchemas);

// src/tenant-api/dto/create-cargo-organization.schema.ts
import { z as z24 } from "zod";
var createCargoOrganizationFieldSchemas = {
  /** Company name */
  companyName: z24.string().min(1),
  /** Address information */
  addressInfo: z24.any().optional(),
  /** Contact person information */
  contactPersonInfo: z24.any().optional()
};
var createCargoOrganizationSchema = z24.object(
  createCargoOrganizationFieldSchemas
);

// src/tenant-api/dto/update-cargo-organization.schema.ts
import { z as z25 } from "zod";
var updateCargoOrganizationSchema = z25.object(
  createCargoOrganizationFieldSchemas
);

// src/tenant-api/dto/upsert-pax.schema.ts
import { z as z26 } from "zod";
var upsertPaxFieldSchemas = {
  /** Pax ID (optional for create) */
  id: z26.string().optional(),
  /** Default values */
  defaultValues: z26.any()
};
var upsertPaxSchema = z26.object(upsertPaxFieldSchemas);

// src/tenant-api/dto/user.schema.ts
import { z as z27 } from "zod";
var userScopeSchema = z27.object({
  roleId: z27.string().min(1),
  scopeId: z27.string().min(1),
  scopeType: z27.string().min(1)
});
var zitadelRoleEnum = z27.enum(["tenant-owner", "tenant-user"]);
var loginUserSchema = z27.object({
  email: z27.string().email().optional(),
  password: z27.string().optional()
});
var createUserSchema = z27.object({
  email: z27.string().email(),
  firstName: z27.string().min(1),
  lastName: z27.string().min(1),
  /** Zitadel role (tenant-owner or tenant-user). Defaults to tenant-user. */
  zitadelRole: zitadelRoleEnum.optional(),
  scopes: z27.array(userScopeSchema).optional()
});
var updateUserSchema = z27.object({
  firstName: z27.string().min(1).optional(),
  lastName: z27.string().min(1).optional(),
  department: z27.string().optional(),
  position: z27.string().optional(),
  email: z27.string().email().optional(),
  phone: z27.string().optional(),
  signatureFileId: z27.string().optional(),
  stampFileId: z27.string().optional()
});

// src/tenant-api/dto/role.schema.ts
import { z as z28 } from "zod";
var createRoleSchema = z28.object({
  name: z28.string().min(1, "Role name is required").max(100, "Role name must be less than 100 characters"),
  description: z28.string().min(1, "Description is required").max(500, "Description must be less than 500 characters"),
  scopeType: z28.string().optional()
});
var updateRoleSchema = createRoleSchema.partial();
var updateRolePermissionsSchema = z28.object({
  permissionIds: z28.array(z28.string())
});

// src/tenant-api/dto/application.schema.ts
import { z as z29 } from "zod";
var createApplicationSchema = z29.object({
  permitName: z29.string().min(1),
  type: z29.string().min(1),
  serviceType: z29.string().min(1),
  country: z29.string().min(1)
});
var updateApplicationSchema = z29.object({
  flightIds: z29.array(z29.string()),
  dataConfig: z29.any().optional(),
  aircraftsConfig: z29.any().optional(),
  documentsConfig: z29.any().optional(),
  formHtml: z29.string().optional()
});
var applyForPermitSchema = z29.object({
  permitConfigName: z29.string().min(1)
});
var applyAircraftsSchema = z29.object({
  flightId: z29.string().min(1),
  permitConfigName: z29.string().min(1)
});
var applyDelaySchema = z29.object({
  flightId: z29.string().min(1),
  permitConfigName: z29.string().min(1),
  newStd: z29.string().min(1)
});
var applyPositioningSchema = z29.object({
  flightId: z29.string().min(1),
  permitConfigName: z29.string().optional(),
  serviceType: z29.string().optional(),
  flightNumber: z29.string().optional(),
  callSign: z29.string().optional(),
  destination: z29.string().optional(),
  std: z29.string().optional(),
  sta: z29.string().optional(),
  reason: z29.string().optional(),
  arrivalFlightId: z29.string().optional(),
  aircraftId: z29.string().optional()
});
var applyRoutesSchema = z29.object({
  flightId: z29.string().min(1),
  permitConfigName: z29.string().min(1)
});
var attachPermitSchema = z29.object({
  permitNumber: z29.string().min(1),
  receivedAt: z29.string().min(1),
  applicationResourceIds: z29.array(z29.string())
});
var updatePermitSchema = z29.object({
  permitNumber: z29.string().min(1).optional(),
  receivedAt: z29.string().min(1).optional(),
  applicationResourceIds: z29.array(z29.string()).optional()
});
var rejectApplicationSubmissionSchema = z29.object({
  title: z29.string().min(1),
  description: z29.string().min(1),
  receivedAt: z29.string().min(1)
});
var sendApplicationSchema = z29.object({
  toAddress: fieldSchemas.optionalEmail,
  channel: z29.enum(["EMAIL", "AFTN", "SITA"]).optional(),
  recipients: z29.string().min(1),
  subject: z29.string().optional(),
  body: z29.string().optional()
});
var saveApplicableChangesetsSchema = z29.object({
  applicableChangesetIds: z29.array(z29.string())
});
var confirmResourcesSchema = z29.object({
  changesetIds: z29.array(z29.string())
});
var saveSelectorChoicesSchema = z29.object({
  selectorChoices: z29.record(z29.string(), z29.number())
});

// src/tenant-api/dto/document.schema.ts
import { z as z30 } from "zod";
var createDocumentSchema = z30.object({
  documentConfigId: z30.string().min(1),
  resourceType: z30.string().optional(),
  resourceId: z30.string().optional(),
  expirationDate: z30.string().optional(),
  fieldsData: z30.string().optional(),
  context: z30.any().optional()
});
var updateDocumentSchema = createDocumentSchema.partial();
var createDocumentAssignmentSchema = z30.object({
  documentId: z30.string().min(1),
  documentConfigId: z30.string().min(1),
  resourceType: z30.string().min(1),
  resourceId: z30.string().min(1),
  context: z30.any().optional()
});
var createDocumentWithAssignmentSchema = z30.object({
  documentConfigId: z30.string().min(1),
  resourceType: z30.string().min(1),
  resourceId: z30.string().min(1),
  expirationDate: z30.string().optional(),
  fieldsData: z30.string().optional(),
  context: z30.any().optional()
});

// src/tenant-api/dto/flight-group.schema.ts
import { z as z31 } from "zod";
var flightGroupsQuerySchema = z31.object({
  activeOnly: z31.union([z31.boolean(), z31.string()]).optional().transform((val) => val === "true" || val === true),
  routeItemStatuses: z31.string().optional().transform(
    (val) => val ? val.split(",").map(Number).filter((n) => !isNaN(n)) : void 0
  ),
  labelIds: z31.string().optional().transform((val) => val ? val.split(",").filter(Boolean) : void 0)
});
var flightsForGroupQuerySchema = z31.object({
  activeOnly: z31.union([z31.boolean(), z31.string()]).optional().transform((val) => val === "true" || val === true)
});

// src/tenant-api/dto/flights-export.schema.ts
import { z as z32 } from "zod";
var flightsExportQuerySchema = z32.object({
  activeOnly: z32.union([z32.boolean(), z32.string()]).optional().transform((val) => val === "true" || val === true),
  routeItemStatuses: z32.string().optional().transform(
    (val) => val ? val.split(",").map(Number).filter((n) => !isNaN(n)) : void 0
  ),
  labelIds: z32.string().optional().transform((val) => val ? val.split(",").filter(Boolean) : void 0),
  seasonType: z32.enum(["summer", "winter"]).optional(),
  searchQuery: z32.string().optional()
});

// src/tenant-api/dto/flight-settings.schema.ts
import { z as z33 } from "zod";
var createFlightAircraftSchema = z33.object({
  aircraftIds: z33.array(z33.string()),
  subTaskId: z33.string().optional()
});
var createFlightCargoSchema = z33.object({
  isCargo: z33.boolean(),
  senderIds: z33.array(z33.string()),
  recipientIds: z33.string(),
  fieldsData: z33.any(),
  subTaskId: z33.string().optional()
});
var createFlightChartererSchema = z33.object({
  chartererIds: z33.array(z33.string()),
  subTaskId: z33.string().optional()
});
var createFlightCrewMemberSchema = z33.object({
  crewMemberIds: z33.array(z33.string()),
  subTaskId: z33.string().optional()
});
var createFlightPaxSchema = z33.object({
  isPax: z33.boolean(),
  subTaskId: z33.string().optional()
});
var createFlightFieldsDataSchema = z33.object({
  fieldsData: z33.any(),
  subTaskId: z33.string().optional()
});
var createFlightSettingsSchema = z33.object({
  serviceType: z33.object({
    fieldsData: z33.any()
  }),
  fleet: z33.object({
    aircraftIds: z33.array(z33.string())
  }),
  cargo: z33.object({
    isCargo: z33.boolean(),
    senderIds: z33.array(z33.string()),
    recipientIds: z33.string(),
    fieldsData: z33.any()
  }),
  charterer: z33.object({
    isCharterer: z33.boolean(),
    chartererIds: z33.array(z33.string())
  }),
  crew: z33.object({
    crewMemberIds: z33.array(z33.string())
  }),
  pax: z33.object({
    isPax: z33.boolean()
  })
});
var updateFlightServiceTypeSchema = z33.object({
  fieldsData: z33.record(z33.string(), z33.any())
});
var updateFlightFlagsSchema = z33.object({
  isPax: z33.boolean().optional(),
  isCargo: z33.boolean().optional(),
  isCharter: z33.boolean().optional()
});
var replaceCargoOrganizationsSchema = z33.object({
  senderIds: z33.array(z33.string()),
  recipientIds: z33.array(z33.string())
});
var updateFlightFieldsDataSchema = z33.object({
  fieldsData: z33.record(z33.string(), z33.any())
});

// src/tenant-api/dto/station-manager.schema.ts
import { z as z34 } from "zod";
var createStationManagerSchema = z34.object({
  firstName: z34.string().optional(),
  lastName: z34.string().optional(),
  defaultFormsOfContact: z34.any().optional(),
  defaultMailingAddress: z34.any().optional()
});
var updateStationManagerSchema = z34.object({
  firstName: z34.string().optional(),
  lastName: z34.string().optional(),
  defaultFormsOfContact: z34.any().optional(),
  defaultMailingAddress: z34.any().optional()
});
var createStationManagerAirportSchema = z34.object({
  airportId: z34.string().min(1),
  notes: z34.string().optional()
});
var updateStationManagerAirportSchema = z34.object({
  airportId: z34.string().optional(),
  notes: z34.string().optional()
});
var createStationManagerCountrySchema = z34.object({
  country: z34.string().min(1),
  mailingAddress: z34.any().optional(),
  formsOfContact: z34.any().optional()
});
var updateStationManagerCountrySchema = z34.object({
  country: z34.string().min(1),
  mailingAddress: z34.any().optional(),
  formsOfContact: z34.any().optional()
});

// src/tenant-api/dto/tenant-profile.schema.ts
import { z as z35 } from "zod";
var createTenantProfileSchema = z35.object({
  name: z35.string().min(1),
  contactInfo: z35.any()
});
var updateTenantProfileSchema = createTenantProfileSchema.partial();

// src/tenant-api/dto/icao-type.schema.ts
import { z as z36 } from "zod";
var createIcaoTypeSchema = z36.object({
  name: z36.string().min(1)
});
var updateIcaoTypeSchema = createIcaoTypeSchema.partial();

// src/tenant-api/dto/setting.schema.ts
import { z as z37 } from "zod";
var importDaysField = z37.number().int().min(0);
var importPolicySchema = z37.discriminatedUnion("mode", [
  z37.object({ mode: z37.literal("WHOLE" /* WHOLE */) }),
  z37.object({
    mode: z37.literal("RANGE" /* RANGE */),
    daysBackward: importDaysField,
    daysForward: importDaysField
  }),
  z37.object({
    mode: z37.literal("MIXED" /* MIXED */),
    daysBackward: importDaysField
  })
]);
var countryConfigSyncNotificationSchema = z37.object({
  enabled: z37.boolean().default(false),
  emails: z37.array(z37.string().email()).max(10).default([])
});
var importCompletionNotificationSchema = z37.object({
  enabled: z37.boolean().default(false),
  emails: z37.array(z37.string().email()).max(10).default([])
});
var notificationPreferencesSchema = z37.object({
  countryConfigSync: countryConfigSyncNotificationSchema.optional(),
  importCompletion: importCompletionNotificationSchema.optional()
});
var createSettingSchema = z37.object({
  system: z37.any().optional(),
  display: z37.any().optional(),
  notifications: notificationPreferencesSchema.optional(),
  import: importPolicySchema.optional()
});
var updateSettingSchema = createSettingSchema.partial();

// src/tenant-api/dto/notification.schema.ts
import { z as z38 } from "zod";
var createNotificationSchema = z38.object({
  userId: z38.string().min(1),
  type: z38.string().min(1),
  data: z38.any().optional(),
  read: z38.boolean().optional()
});
var updateNotificationSchema = z38.object({
  read: z38.boolean().optional()
});

// src/tenant-api/dto/comment.schema.ts
import { z as z39 } from "zod";
var mentionSchema = z39.object({
  userId: z39.string().min(1),
  displayName: z39.string().min(1)
});
var createCommentSchema = z39.object({
  text: z39.string().min(1),
  resourceId: z39.string().min(1),
  resourceType: z39.string().min(1),
  mentions: z39.array(mentionSchema).max(20).optional().default([])
});
var updateCommentSchema = createCommentSchema.partial();

// src/tenant-api/dto/batch-operation.schema.ts
import { z as z40 } from "zod";
var createBatchOperationSchema = z40.object({
  tenantSlug: z40.string().min(1),
  type: z40.string().min(1),
  createdBy: z40.string().min(1),
  organizationSlug: z40.string().optional(),
  config: z40.record(z40.string(), z40.any()).optional()
});

// src/tenant-api/dto/analytics.schema.ts
import { z as z41 } from "zod";
var createCustomEventTypeSchema = z41.object({
  resourceType: z41.string().min(1),
  name: z41.any()
});
var createEventLogSchema = z41.object({
  resourceId: z41.string().optional(),
  resourceType: z41.string().optional(),
  type: z41.string().optional(),
  customTypeId: z41.string().optional(),
  title: z41.string().min(1),
  description: z41.string().optional(),
  data: z41.any().optional(),
  createdAt: z41.coerce.date().optional(),
  createdBy: z41.string().optional()
});

// src/tenant-api/dto/specific-data.schema.ts
import { z as z42 } from "zod";
var createSpecificDataSchema = z42.object({
  taskId: z42.string().optional(),
  flights: z42.array(z42.string()),
  fields: z42.array(z42.string()),
  documents: z42.array(z42.string())
});
var updateSpecificDataSchema = z42.object({
  flights: z42.array(z42.string()),
  fields: z42.array(z42.string()),
  documents: z42.array(z42.string())
});
var createFlightDocumentsSchema = z42.object({
  flightId: z42.string().min(1),
  resourceId: z42.string().optional(),
  resourceType: z42.string().optional(),
  documents: z42.array(
    z42.object({
      documentConfigId: z42.string().min(1),
      documentId: z42.string().min(1)
    })
  )
});
var updateFlightDocumentsSchema = z42.object({
  documents: z42.record(z42.string(), z42.string())
});
var createFlightFieldDataSchema = z42.object({
  flightId: z42.string().min(1),
  fieldsData: z42.any()
});
var updateFlightFieldDataSchema = z42.object({
  fieldsData: z42.any()
});

// src/tenant-api/dto/city-pair.schema.ts
import { z as z43 } from "zod";
var createCityPairAircraftsSchema = z43.object({
  aircraftIds: z43.array(z43.string()),
  mainAircraftIds: z43.array(z43.string())
});

// src/tenant-api/dto/used-service-provider.schema.ts
import { z as z44 } from "zod";
var createUsedServiceProviderSchema = z44.object({
  usedAirportId: z44.string().min(1),
  serviceProviderId: z44.array(z44.string()).nonempty(),
  serviceSlug: z44.string().min(1),
  notes: z44.string().optional()
});

// src/tenant-api/dto/service-type.schema.ts
import { z as z45 } from "zod";
var upsertServiceTypeSettingSchema = z45.object({
  serviceType: z45.string().min(1),
  purposeOfFlight: z45.string().optional()
});

// src/tenant-api/dto/default-data-setting.schema.ts
import { z as z46 } from "zod";
var defaultCrewEntrySchema = z46.object({
  crewMemberId: z46.string(),
  icaoTypeId: z46.string(),
  department: z46.string()
  // "schedule" | "nonSchedule" | "other"
});
var defaultCrewConfigSchema = z46.object({
  pilots: z46.array(defaultCrewEntrySchema).default([]),
  cabinCrewMembers: z46.array(defaultCrewEntrySchema).default([]),
  cockpitCrewMembers: z46.array(defaultCrewEntrySchema).default([])
});
var upsertDefaultDataSettingSchema = z46.object({
  // Crew defaults (per ICAO type × department)
  defaultCrewConfig: defaultCrewConfigSchema.nullable().optional(),
  // Cargo organization defaults (separated by role)
  defaultCargoSenderIds: z46.array(z46.string()).default([]),
  defaultCargoRecipientIds: z46.array(z46.string()).default([]),
  // Charterer defaults
  defaultChartererIds: z46.array(z46.string()).default([]),
  // Cargo & Pax field data
  defaultCargoFieldsData: z46.any().nullable().optional(),
  defaultPaxFieldsData: z46.any().nullable().optional(),
  // Array selector choices (which contact person, phone, etc. to use)
  defaultSelectorChoices: z46.record(z46.string(), z46.number()).nullable().optional()
});

// src/tenant-api/dto/country-config.schema.ts
import { z as z47 } from "zod";
var createCountryConfigFieldSchemas = {
  /** ISO 2-letter country code (lowercase), e.g., 'pl', 'us' */
  countrySlug: fieldSchemas.countrySlug,
  /** ICAO country code (2 letters), e.g., 'EP' for Poland */
  icaoCode: fieldSchemas.icaoCode.optional(),
  /** IATA country code (2 letters), e.g., 'PL' for Poland */
  iataCode: fieldSchemas.iataCode.optional()
  // ❌ NO airacCycleNumber - backend determines it automatically
};
var createCountryConfigSchema = z47.object(
  createCountryConfigFieldSchemas
);

// src/tenant-api/dto/document-type.schema.ts
import { z as z48 } from "zod";
import { DocumentResourceType } from "@repo/data";
var createDocumentTypeFieldSchemas = {
  /** Unique name identifier for the document type */
  name: z48.string().min(1, "Name is required"),
  /** Human-readable label for the document type */
  label: z48.string().min(1, "Label is required"),
  /** Type classification of the document */
  type: z48.string().min(1, "Type is required"),
  /** Optional description of the document type */
  description: z48.string().optional(),
  /** Optional grouping category */
  group: z48.string().optional(),
  /** Resource type this document is associated with */
  resourceType: z48.nativeEnum(DocumentResourceType).optional(),
  /** Whether this document type has an expiration date */
  hasExpirationDate: z48.boolean().optional().default(false),
  /** JSON field definitions for the document type */
  fields: z48.any().optional()
};
var createDocumentTypeSchema = z48.object(
  createDocumentTypeFieldSchemas
);
var updateDocumentTypeFieldSchemas = {
  /** Unique name identifier for the document type */
  name: z48.string().min(1, "Name cannot be empty").optional(),
  /** Human-readable label for the document type */
  label: z48.string().min(1, "Label cannot be empty").optional(),
  /** Type classification of the document */
  type: z48.string().min(1, "Type cannot be empty").optional(),
  /** Optional description of the document type */
  description: z48.string().optional(),
  /** Optional grouping category */
  group: z48.string().optional(),
  /** Resource type this document is associated with */
  resourceType: z48.nativeEnum(DocumentResourceType).optional(),
  /** Whether this document type has an expiration date */
  hasExpirationDate: z48.boolean().optional(),
  /** JSON field definitions for the document type */
  fields: z48.any().optional()
};
var updateDocumentTypeSchema = z48.object(
  updateDocumentTypeFieldSchemas
);

// src/tenant-api/responses/batch-operation.response.ts
var BatchOperationStatus = /* @__PURE__ */ ((BatchOperationStatus2) => {
  BatchOperationStatus2["PENDING"] = "PENDING";
  BatchOperationStatus2["PROCESSING"] = "PROCESSING";
  BatchOperationStatus2["AWAITING_USER_ACTION"] = "AWAITING_USER_ACTION";
  BatchOperationStatus2["COMPLETED"] = "COMPLETED";
  BatchOperationStatus2["FAILED"] = "FAILED";
  BatchOperationStatus2["CANCELLED"] = "CANCELLED";
  return BatchOperationStatus2;
})(BatchOperationStatus || {});
export {
  APPLICATION_TASK_TYPES,
  ApplicationSubmissionStatus,
  ApplicationSubmissionType,
  BatchOperationStatus,
  CountryConfigSource,
  CountryConfigStatus,
  EventLogResourceType,
  EventLogType,
  NoteLevel,
  PermitConfigSource,
  apmConfigFieldSchemas,
  apmConfigSchema,
  applyAircraftsSchema,
  applyDelaySchema,
  applyForPermitSchema,
  applyPositioningSchema,
  applyRoutesSchema,
  attachPermitSchema,
  confirmResourcesSchema,
  countryConfigSyncNotificationSchema,
  createAircraftFieldSchemas,
  createAircraftSchema,
  createApplicationSchema,
  createBatchOperationSchema,
  createCargoOrganizationFieldSchemas,
  createCargoOrganizationSchema,
  createChartererFieldSchemas,
  createChartererSchema,
  createCityPairAircraftsSchema,
  createCommentSchema,
  createCountryConfigFieldSchemas,
  createCountryConfigSchema,
  createCountryConfigScopeFieldSchemas,
  createCountryConfigScopeSchema,
  createCrewFieldSchemas,
  createCrewSchema,
  createCustomEventTypeSchema,
  createDocumentAssignmentSchema,
  createDocumentSchema,
  createDocumentTypeFieldSchemas,
  createDocumentTypeSchema,
  createDocumentWithAssignmentSchema,
  createEventLogSchema,
  createFlightAircraftSchema,
  createFlightCargoSchema,
  createFlightChartererSchema,
  createFlightCrewMemberSchema,
  createFlightDocumentsSchema,
  createFlightFieldDataSchema,
  createFlightFieldsDataSchema,
  createFlightPaxSchema,
  createFlightSettingsSchema,
  createIcaoTypeSchema,
  createNoteFieldSchemas,
  createNoteSchema,
  createNotificationSchema,
  createOrganizationFieldSchemas,
  createOrganizationSchema,
  createPermitConfigFieldSchemas,
  createPermitConfigSchema,
  createRoleSchema,
  createSettingSchema,
  createSpecificDataSchema,
  createStationManagerAirportSchema,
  createStationManagerCountrySchema,
  createStationManagerSchema,
  createSubTaskFieldSchemas,
  createSubTaskSchema,
  createTaskFieldSchemas,
  createTaskLabelFieldSchemas,
  createTaskLabelSchema,
  createTaskSchema,
  createTenantAirportFieldSchemas,
  createTenantAirportSchema,
  createTenantFirFieldSchemas,
  createTenantFirSchema,
  createTenantProfileSchema,
  createUsedServiceProviderSchema,
  createUserSchema,
  defaultCrewConfigSchema,
  defaultCrewEntrySchema,
  fieldSchemas,
  flightGroupsQuerySchema,
  flightsExportQuerySchema,
  flightsForGroupQuerySchema,
  importCompletionNotificationSchema,
  importPolicySchema,
  isApplicationTaskType,
  labelResourceTypes,
  loginUserSchema,
  mentionSchema,
  notificationPreferencesSchema,
  rejectApplicationSubmissionSchema,
  reorderSubTasksSchema,
  replaceCargoOrganizationsSchema,
  saveApplicableChangesetsSchema,
  saveSelectorChoicesSchema,
  sendApplicationSchema,
  setFlightLabelsSchema,
  smtpConfigFieldSchemas,
  smtpConfigSchema,
  specificDataSchema,
  subTaskSchema,
  taskTypeEnum,
  tenantApiRoutes,
  updateAircraftSchema,
  updateApplicationSchema,
  updateCargoOrganizationSchema,
  updateChartererSchema,
  updateCommentSchema,
  updateCountryConfigSchema,
  updateCountryConfigScopeFieldSchemas,
  updateCountryConfigScopeSchema,
  updateCrewFieldSchemas,
  updateCrewSchema,
  updateDocumentSchema,
  updateDocumentTypeFieldSchemas,
  updateDocumentTypeSchema,
  updateFlightDocumentsSchema,
  updateFlightFieldDataSchema,
  updateFlightFieldsDataSchema,
  updateFlightFlagsSchema,
  updateFlightServiceTypeSchema,
  updateIcaoTypeSchema,
  updateNoteSchema,
  updateNotificationSchema,
  updateOrganizationSchema,
  updatePermitConfigFieldSchemas,
  updatePermitConfigSchema,
  updatePermitSchema,
  updateRolePermissionsSchema,
  updateRoleSchema,
  updateSettingSchema,
  updateSpecificDataSchema,
  updateStationManagerAirportSchema,
  updateStationManagerCountrySchema,
  updateStationManagerSchema,
  updateSubTaskFieldSchemas,
  updateSubTaskSchema,
  updateTaskFieldSchemas,
  updateTaskLabelFieldSchemas,
  updateTaskLabelSchema,
  updateTaskSchema,
  updateTenantAirportFieldSchemas,
  updateTenantAirportSchema,
  updateTenantFirFieldSchemas,
  updateTenantFirSchema,
  updateTenantProfileSchema,
  updateUserSchema,
  upsertCargoFieldSchemas,
  upsertCargoSchema,
  upsertCountryConfigCustomizationFieldSchemas,
  upsertCountryConfigCustomizationSchema,
  upsertDefaultDataSettingSchema,
  upsertPaxFieldSchemas,
  upsertPaxSchema,
  upsertPermitConfigCustomizationFieldSchemas,
  upsertPermitConfigCustomizationSchema,
  upsertServiceTypeSettingSchema,
  upsertTenantIntegrationFieldSchemas,
  upsertTenantIntegrationSchema,
  zitadelRoleEnum
};
